import React, { useEffect, useState } from 'react';
import StepContentRenderer from '../steps-content/StepContentRenderer';
import FinalStepContent from '../steps-content/final-step/FinalStepContent';
import { Icon, Spinner } from '@gs/fe-storybook-gasco';
import {
  ValidationStepsProps,
  ValidationData,
  Question,
} from '../../../interfaces/interfaceSteps';
import { sendVerificationCode } from '../../../config/services/steps/sendCode';
import { validateCode } from '../../../config/services/steps/validateCode';
import { generateHash } from '../../../config/services/steps/hash';
import { firstQuestionnaire } from '../../../config/services/steps/firstQuestionnaire';
import { eventEmitter } from '../steps-content/code-step/EventEmitter';
import Code from '../../../assets/images/code.svg';
import Phone from '../../../assets/images/phone.svg';
import Rut from '../../../assets/images/rut.svg';
import './ValidationSteps.css';

const ValidationSteps: React.FC<ValidationStepsProps> = ({
  step,
  data,
  onChange,
  onNext,
  onPrevious,
  redirectToOtherView,
}) => {
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [sentPhoneNumber, setSentPhoneNumber] = useState<string | null>(null);
  const [isCodeIncorrect, setIsCodeIncorrect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fadeClass, setFadeClass] = useState('');
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [rutError, setRutError] = useState<string>('');
  const [documentNumberError, setDocumentNumberError] = useState<string>('');
  const [questionnaireDescription, setQuestionnaireDescription] =
    useState<string>('');
  const [questionnaireCode, setQuestionnaireCode] = useState<string>('');
  const [questionnaireHash, setQuestionnaireHash] = useState<string>('');

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isServiceExecuted, setIsServiceExecuted] = useState<boolean>(false);
  const [preguntas, setPreguntas] = useState<Question[]>([]);
  const [rut, setRut] = useState('');
  const [dv, setDv] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [hash, setHash] = useState('');
  const [isRutAndDocValid, setIsRutAndDocValid] = useState<boolean>(false);
  const [sendCodeError, setSendCodeError] = useState<string | null>(null);
  const [isFinal, setIsFinal] = React.useState(false);
  const [isResending, setIsResending] = useState(false);
  
  useEffect(() => {
    const handleIsResendingChange = (newIsResending: boolean) => {
      setIsResending(newIsResending);
    };

    eventEmitter.on<boolean>('isResendingChange', handleIsResendingChange);

    return () => {
      eventEmitter.off('isResendingChange');
    };
  }, []);

  useEffect(() => {
    if (hash) {
      const nroSerie = documentNumber;
      executeFirstQuestionnaire(hash, nroSerie);
    }
  }, [hash]);

  useEffect(() => {
    setIsServiceExecuted(false);
    setQuestionnaireDescription('');
    setQuestionnaireCode('');
    setQuestionnaireHash('');
  }, []);

  useEffect(() => {
    setFadeClass('fade-in');
    const timer = setTimeout(() => setFadeClass(''), 1000);
    return () => clearTimeout(timer);
  }, [step]);

  const stepConfigs = [
    {
      dataKey: 'phone',
      imageSrc: Phone,
      title: 'Ingresa tu número de teléfono',
      description: 'Agrega el número telefónico que utilizas actualmente',
    },
    {
      dataKey: 'code',
      imageSrc: Code,
      title: 'Ingresa tu número de verificación',
      description: `Ingresa el código que te enviamos por SMS al +56 ${
        sentPhoneNumber || '+56 000000000'
      }`,
    },
    {
      dataKey: 'rut',
      imageSrc: Rut,
      title: 'Ingresa el RUT que deseas usar',
      description: 'Agrega el rut nuevo que utilizarás de ahora en adelante',
    },
    {
      dataKey: 'questions',
    },
    {
      dataKey: 'final',
    },
  ];

  const placeholders: Record<
    Exclude<keyof ValidationData, 'success'>,
    string
  > = {
    phone: 'Número de teléfono',
    code: '',
    rut: 'RUT nuevo',
    documentNumber: 'Número de documento',
    dv: '',
  };

  const handlePreviousStep = () => {
    if (step === 2) {
      setIsCodeIncorrect(false);
    }
    onPrevious();
  };

  const renderButtonText = () => {
    const texts = [
      'Enviar código de validación',
      'Validar',
      'Continuar',
      data.success ? 'Redireccionarse ahora' : 'Cerrar',
    ];
    return texts[step - 1] || 'Siguiente';
  };

  const handleSendCode = async () => {
    setIsLoading(true);
    setSendCodeError(null);
    try {
      await sendVerificationCode(data.phone);
      setSentPhoneNumber(data.phone);
      onNext();
      setIsCodeIncorrect(false);
    } catch (error) {
      const codeErrorMessage =
        'Actualmente estamos presentando intermitencias de envíos de nuestros SMS.';

      console.error('Error al enviar el código de verificación:', error);

      setSendCodeError(codeErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidateCode = async () => {
    setIsLoading(true);
    setIsCodeIncorrect(false);

    try {
      const code = Array.isArray(data.code)
        ? data.code.join('')
        : data.code || '';

      if (!sentPhoneNumber) {
        console.error('El número de teléfono no ha sido enviado');
        setIsCodeIncorrect(true);
        return;
      }

      const response = await validateCode(sentPhoneNumber, code, isResending);

      if (response && response.data && response.data.status === 'Approved') {
        setIsCodeIncorrect(false);
        onNext();
      } else {
        setIsCodeIncorrect(true);
      }
    } catch (error) {
      console.error('Error al validar el código:', error);
      setIsCodeIncorrect(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (newPhoneValue: string) => {
    const isValid = newPhoneValue.length === 9 && newPhoneValue.startsWith('9');
    setIsPhoneValid(isValid);
    onChange('phone', newPhoneValue);
  };

  const handleCheckboxChange = (newValue: boolean) => {
    setIsCheckboxChecked(newValue);
  };

  const handleCodeChange = (newCode: string[]) => {
    const allFilled = newCode.every((char) => char !== '');
    setIsCodeValid(allFilled);
    onChange('code', newCode);
    if (newCode.join('') !== '') {
      setIsCodeIncorrect(false);
    }
  };

  const handleValidateRut = (rutValue: string) => {
    const cleanRut = rutValue.replace(/[^0-9kK-]/g, '');
    const rutParts = cleanRut.split('-');
    const rutNumbers = rutParts[0].replace(/\./g, '');
    const rutDv = rutParts[1]?.toUpperCase();

    let validationError = '';

    if (rutNumbers.length < 7 || rutNumbers.length > 8) {
      validationError = rutValue
        ? 'El formato ingresado no es el correcto'
        : '';
      setRutError(validationError);
      return false;
    }

    if (!/^[0-9Kk]$/.test(rutDv || '')) {
      validationError = rutValue ? 'El dígito verificador no es válido' : '';
      setRutError(validationError);
      return false;
    }

    setRut(rutNumbers);
    setDv(rutDv);
    setRutError('');
    return true;
  };
  const handleValidateDocumentNumber = (docValue: string) => {
    const documentRegex = /^(A\d{9}|\d{9}|B([0-4]|[5-9C-Z])[B-Z0-9]\d{6})$/;

    const cleanDoc = docValue.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    setDocumentNumber(cleanDoc);

    if (!documentRegex.test(cleanDoc)) {
      setDocumentNumberError(
        docValue ? 'El formato ingresado no es el correcto' : ''
      );
      return false;
    }

    setDocumentNumberError('');
    return true;
  };

  const handleGenerateHash = async () => {
    try {
      const telefono = data.phone;
      const payload = { rut, dv, telefono };

      const response = await generateHash(
        payload.rut,
        payload.dv,
        payload.telefono
      );

      if (response && response.data) {
        const generatedHash = response.data;
        setHash(generatedHash);
        setErrorMessage('');
      } else {
        throw new Error('El hash no se pudo obtener.');
      }
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      const message = errorResponse?.message || 'Error desconocido';
      setErrorMessage(message);
      throw error;
    }
  };

  const executeFirstQuestionnaire = async (
    hashToExecute: string,
    nroSerie: string
  ) => {
    try {
      const firstDocumentNumber = nroSerie;
      await handleFirstQuestionnaire(hashToExecute, firstDocumentNumber);
    } catch (error) {
      console.error('Error al ejecutar el cuestionario:', error);
    }
  };

  const handleFirstQuestionnaire = async (
    firstQuestionnaireHash: string,
    nroSerie: string
  ) => {
    try {
      setIsLoading(true);
      const response = await firstQuestionnaire(
        firstQuestionnaireHash,
        nroSerie
      );

      if (response?.data) {
        const code = response.data.codigo;
        const description = response.data.descripcion;
        const hashError = response.data.hash;
        setQuestionnaireDescription(description);
        setQuestionnaireCode(code);
        setQuestionnaireHash(hashError);

        const receivedQuestions = response.data.preguntas;
        setPreguntas(receivedQuestions);
        setIsServiceExecuted(true);

        if (receivedQuestions && receivedQuestions.length > 0) {
          onNext();
        }
      }
    } catch (error) {
      console.error('Error al obtener el primer cuestionario:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRutAndDocValidityChange = (valid: boolean) => {
    setIsRutAndDocValid(valid);
  };

  const renderStepContent = () => {
    const { imageSrc, title, description, dataKey } = stepConfigs[step - 1];
    const value = data[dataKey as keyof ValidationData];
    const inputValue = value?.toString() || '';

    return (
      <StepContentRenderer
        imageSrc={imageSrc}
        title={title}
        description={description}
        errorDescription={questionnaireDescription || description}
        value={inputValue}
        onChange={(newValue) => {
          if (dataKey === 'rut' && typeof newValue === 'string') {
            handleValidateRut(newValue);
          } else if (dataKey === 'code') {
            const codeArray = Array.isArray(newValue)
              ? newValue
              : newValue.split('');
            handleCodeChange(codeArray);
          } else if (dataKey === 'phone' && typeof newValue === 'string') {
            handlePhoneChange(newValue);
          } else if (
            dataKey === 'documentNumber' &&
            typeof newValue === 'string'
          ) {
            handleValidateDocumentNumber(newValue);
          }
        }}
        step={step}
        placeholders={placeholders}
        onCheckboxChange={handleCheckboxChange}
        isCheckboxChecked={isCheckboxChecked}
        isCodeIncorrect={isCodeIncorrect}
        validateRut={handleValidateRut}
        validateDocumentNumber={handleValidateDocumentNumber}
        rutError={rutError}
        documentNumberError={documentNumberError}
        preguntas={preguntas}
        hash={hash}
        onRutAndDocValidityChange={handleRutAndDocValidityChange}
        onFinalStepChange={(finalState) => setIsFinal(finalState)}
        setIsServiceExecuted={setIsServiceExecuted}
        setErrorMessage={setErrorMessage}
      />
    );
  };

  const renderFinalContent = () => {
    return (
      <FinalStepContent
        status="error"
        responseCode="ERROR_CODE"
        responseHash="HASH_VALUE"
      />
    );
  };

  const renderContent = () => {
    return step <= stepConfigs.length
      ? renderStepContent()
      : renderFinalContent();
  };

  const disabledButton = () => {
    if (step === 1) {
      return !isPhoneValid || !isCheckboxChecked;
    } else if (step === 2) {
      return !isCodeValid || isCodeIncorrect;
    } else if (step === 3) {
      const isRutValid = !rutError;
      const isDocumentNumberValid = !documentNumberError;
      return !isRutValid || !isDocumentNumberValid || !isRutAndDocValid;
    }
  };

  const onButtonClick = async () => {
    setIsLoading(true);
    try {
      if (step === 1) {
        await handleSendCode();
      } else if (step === 2) {
        await handleValidateCode();
      } else if (step === 3) {
        await handleGenerateHash();
      }
    } catch (error) {
      console.error('Error durante la ejecución:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = () => {
    setErrorMessage('');
    setRutError('');
    setDocumentNumberError('');
    onButtonClick();
  };

  const getButtonLabel = (currentStep: number): string => {
    return currentStep === 1
      ? 'Enviar código de validación'
      : renderButtonText();
  };
  const isButtonDisabled = disabledButton();
  const buttonClass = `stepsButtons${
    isButtonDisabled ? ' stepDisabledButton' : ''
  }`;

  let buttonId;
  if (step === 1) {
    buttonId = 'numero_enviarcodigo';
  } else if (step === 2) {
    buttonId = 'codigo_validar';
  } else if (step === 3) {
    buttonId = 'rut_continuar';
  }

  const getIndicatorClass = (s: number) => {
    const isActive =
      (step === 1 && s === 1) ||
      (step === 2 && (s === 1 || s === 2)) ||
      (step >= 3 && s !== 4) ||
      (isFinal && s <= 4);

    const isNextStep = step === s - 1 || (step === 4 && s === 4);

    return `indicator${isActive ? ' active' : ''}${
      isNextStep ? ' next-step' : ''
    }${isFinal ? ' all-active' : ''}`;
  };

  return (
    <div className="step-container">
      {step === 1 && sendCodeError && (
        <div className="error-message">
          <Icon className="exclamation-icon" name="exclamation-circle" />
          {sendCodeError}
        </div>
      )}
      <div className="row step-header" data-step={step}>
        <div className="col-4">
          {(step === 1 || step === 2) && (
            <button
              className="back-button"
              onClick={step === 1 ? redirectToOtherView : handlePreviousStep}
            >
              <Icon name="arrow-left" />
            </button>
          )}
        </div>
        <div className="col-4 step-indicators">
          {[1, 2, 3, 4].map((s) => (
            <span key={s} className={getIndicatorClass(s)}></span>
          ))}
        </div>
        <div className="col-4"></div>
      </div>
      <div className={`step-content ${fadeClass}`}>{renderContent()}</div>
      {step === 3 &&
        (errorMessage ? (
          <div className="errors">{errorMessage}</div>
        ) : (
          isServiceExecuted && (
            <div>
              <p className="errors">{questionnaireDescription}</p>
              <p className="codeErrors">{questionnaireCode}</p>
              <p className="hashErrors">{questionnaireHash}</p>
            </div>
          )
        ))}
      <br />
      {isLoading ? (
        <div className="spinner-container">
          <Spinner size="large" />
        </div>
      ) : (
        step !== 4 && (
          <button
            className={buttonClass}
            id={buttonId}
            onClick={handleButtonClick}
            disabled={isButtonDisabled}
          >
            {getButtonLabel(step)}
          </button>
        )
      )}
    </div>
  );
};

export default ValidationSteps;
